'use client';

import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import type { Lang } from '@/lib/i18n/translations';

interface LanguageSwitcherProps {
  // Allows callers (e.g. the dashboard, which supports a light theme) to
  // adapt colors to their surrounding context without forking the
  // component. Defaults match the dark, purple-accented booking pages.
  activeColor?: string;
  inactiveColor?: string;
  trackBackground?: string;
  trackBorder?: string;
}

export default function LanguageSwitcher({
  activeColor = '#fff',
  inactiveColor = 'rgba(255,255,255,0.4)',
  trackBackground = 'rgba(255,255,255,0.06)',
  trackBorder = 'rgba(255,255,255,0.12)',
}: LanguageSwitcherProps) {
  const { lang, setLang } = useLanguage();

  const optionStyle = (value: Lang): React.CSSProperties => ({
    background: lang === value ? 'rgba(124,58,237,0.5)' : 'transparent',
    border: 'none',
    color: lang === value ? activeColor : inactiveColor,
    fontSize: '12px',
    fontWeight: 700,
    letterSpacing: '0.02em',
    cursor: 'pointer',
    padding: '4px 9px',
    borderRadius: '6px',
    transition: 'all 0.15s',
  });

  return (
    <div
      role="group"
      aria-label="Language"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '2px',
        background: trackBackground,
        border: `1px solid ${trackBorder}`,
        borderRadius: '8px',
        padding: '2px',
      }}
    >
      <button
        type="button"
        onClick={() => setLang('sq')}
        aria-pressed={lang === 'sq'}
        style={optionStyle('sq')}
      >
        SQ
      </button>
      <button
        type="button"
        onClick={() => setLang('en')}
        aria-pressed={lang === 'en'}
        style={optionStyle('en')}
      >
        EN
      </button>
    </div>
  );
}
